import { useRouter } from "@tanstack/react-router"
import type { CabinetMfaSecret } from "@planerius/contracts/cabinet-auth"
import type { FormEvent, ReactNode } from "react"
import { useCallback, useEffect, useState } from "react"

import { accessStore } from "../../access/access-store"
import { confirmMfaEnrollment } from "../../api/auth.api"
import { enrollMfa } from "../../api/mfa.api"
import type { ApiProblem } from "../../api/problem"
import { isApiProblem, transportProblem } from "../../api/problem"
import { nextNeedsNoTenant, safeNextPath } from "../../app/navigate-next"
import { QrCode } from "../../ui"
import { codeError, recoveryCodesText, secretGroups } from "../profile/mfa-model"
import styles from "../auth.module.css"
import { enrollChallengeStore } from "./enroll-challenge"

/**
 * Э1.2-б «Заведите второй фактор» — шаг входа, а не настройка.
 *
 * ЗАЧЕМ ОТДЕЛЬНЫЙ ЭКРАН. Роль в салоне требует второго фактора, а у человека его нет:
 * пароль верный, но внутрь нельзя. Раньше здесь был отказ со словами «настройте по ссылке
 * из письма» — ссылки такой нет ни в одном письме, и человек оставался снаружи насовсем.
 * Теперь это ход: показываем QR, принимаем код, выдаём резервные коды и впускаем.
 *
 * ОТМЕНЫ ЗДЕСЬ НЕТ, И ЭТО НЕ НЕДОСМОТР. Единственный способ не завести фактор — уйти
 * с экрана, то есть не войти. Кнопка «Отмена», ведущая обратно к паролю, обещала бы
 * другой путь внутрь, которого не существует; вместо неё — «Выйти», честно возвращающая
 * к форме входа.
 *
 * КОДЫ ПОКАЗЫВАЮТСЯ ДО ВХОДА В КАБИНЕТ. Сессия к этому моменту уже выдана — но пока
 * человек не нажал «Я сохранил коды», мы его никуда не уводим: список больше не покажет
 * никто, и увести с него на журнал значит отобрать единственный шанс их сохранить.
 */
export function EnrollMfaScreen(props: { readonly next: string | undefined }): ReactNode {
  const router = useRouter()
  const enrollment = enrollChallengeStore.read()
  const [secret, setSecret] = useState<CabinetMfaSecret | null>(null)
  const [codes, setCodes] = useState<readonly string[] | null>(null)
  const [code, setCode] = useState("")
  const [showError, setShowError] = useState(false)
  const [problem, setProblem] = useState<ApiProblem | null>(null)
  const [busy, setBusy] = useState(false)

  /*
   * ВОЗВРАТ К ПАРОЛЮ — ТОЛЬКО ПОКА КОДЫ НЕ ПОКАЗАНЫ.
   *
   * Условие `codes === null` здесь главное. Без него происходило вот что: подтверждение
   * гасило билет, билет становился null, этот эффект немедленно уводил на /login —
   * и десять резервных кодов, которые больше не покажет никто, исчезали вместе
   * с экраном. Человек включал второй фактор и в ту же секунду терял единственный
   * способ войти без телефона.
   */
  useEffect(() => {
    if (enrollment === null && codes === null) {
      void router.navigate({ to: "/login", search: { next: props.next } })
    }
  }, [codes, enrollment, props.next, router])

  // Секрет выпускается один раз на открытие экрана. Повторный вызов выдал бы НОВЫЙ
  // секрет и погасил тот, который человек только что отсканировал.
  useEffect(() => {
    if (enrollment === null || secret !== null || busy) return
    let alive = true
    setBusy(true)
    void enrollMfa(enrollment.enrollToken)
      .then((issued) => {
        if (alive) setSecret(issued)
      })
      .catch((error: unknown) => {
        if (alive) setProblem(isApiProblem(error) ? error : transportProblem("unknown"))
      })
      .finally(() => {
        if (alive) setBusy(false)
      })
    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- намеренно один раз на экран
  }, [])

  const onSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>): Promise<void> => {
      event.preventDefault()
      if (enrollment === null) return
      setShowError(true)
      if (codeError(code, "totp") !== null) return
      setBusy(true)
      setProblem(null)
      try {
        const result = await confirmMfaEnrollment(enrollment.enrollToken, code)
        // Сначала коды на экран, и только потом гасим билет: обратный порядок уводил
        // человека на /login эффектом выше — до того, как он увидел хоть один код.
        setCodes(result.recoveryCodes)
        enrollChallengeStore.clear()
      } catch (error) {
        setProblem(isApiProblem(error) ? error : transportProblem("unknown"))
      } finally {
        setBusy(false)
      }
    },
    [code, enrollment],
  )

  const enter = useCallback(async (): Promise<void> => {
    const next = await accessStore.reload()
    if (next.kind === "tenant_required" && !nextNeedsNoTenant(props.next)) {
      await router.navigate({ to: "/choose-salon", search: { next: props.next } })
      return
    }
    router.history.push(safeNextPath(props.next, "/"))
  }, [props.next, router])

  if (codes !== null) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <div className={styles.brand}>planerius</div>
          <h1 className={styles.title}>Сохраните резервные коды</h1>
          <p className={styles.subtitle}>
            Второй фактор включён. Каждый код срабатывает один раз — им вы войдёте, если
            потеряете телефон. Больше мы их не покажем.
          </p>
          <ul className={styles.codes}>
            {codes.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <div className={styles.field}>
            <button
              className={styles.linkButton}
              type="button"
              onClick={() => {
                void navigator.clipboard?.writeText(recoveryCodesText(codes, new Date())).catch(() => {
                  // Буфер может быть запрещён — коды на экране, человек перепишет.
                })
              }}
            >
              Скопировать
            </button>
          </div>
          <button className={styles.submit} type="button" onClick={() => void enter()}>
            Я сохранил коды, войти
          </button>
        </div>
      </div>
    )
  }

  if (enrollment === null) return null

  return (
    <div className={styles.page}>
      <form
        className={styles.card}
        onSubmit={(event) => {
          void onSubmit(event)
        }}
      >
        <div className={styles.brand}>planerius</div>
        <h1 className={styles.title}>Заведите второй фактор</h1>
        <p className={styles.subtitle}>
          Ваша роль в салоне требует подтверждения входа кодом. Установите приложение-
          аутентификатор (Яндекс.Ключ, Google Authenticator, 1Password), наведите камеру
          на код — и введите шесть цифр из приложения.
        </p>

        {problem === null ? null : (
          <div className={styles.error} role="alert">
            <div className={styles.errorTitle}>{problem.title}</div>
            {problem.detail ?? "Проверьте код и попробуйте ещё раз."}
          </div>
        )}

        {secret === null ? (
          <p className={styles.subtitle}>Готовим код…</p>
        ) : (
          <>
            <QrCode text={secret.otpauthUri} label="Код для приложения-аутентификатора" />
            <p className={styles.secret}>
              {secretGroups(secret.secret).map((group) => (
                <span key={group}>{group}</span>
              ))}
            </p>
          </>
        )}

        <div className={styles.field}>
          <label className={styles.label} htmlFor="enroll-code">
            Код из приложения
          </label>
          <input
            id="enroll-code"
            className={styles.input}
            inputMode="numeric"
            autoComplete="one-time-code"
            required
            value={code}
            onChange={(event) => {
              setCode(event.target.value)
            }}
          />
          {showError && codeError(code, "totp") !== null ? (
            <span className={styles.hint}>{codeError(code, "totp")}</span>
          ) : null}
        </div>

        <button className={styles.submit} type="submit" disabled={busy || secret === null}>
          {busy ? "Проверяем…" : "Включить и войти"}
        </button>
        <button
          className={styles.linkButton}
          type="button"
          onClick={() => {
            enrollChallengeStore.clear()
            void router.navigate({ to: "/login", search: { next: props.next } })
          }}
        >
          Выйти
        </button>
      </form>
    </div>
  )
}
